import groupBy from 'lodash/groupBy'
import head from 'lodash/head'
import last from 'lodash/last'

import {
  contactLinkTypes,
  phoneVisibleCountryList,
  fileAttachmentVisibleCountryList,
  countriesWithTwothousandDigitsInTopic,
  countriesWithMandatoryNameAndSurnameField,
} from '@/scripts/constants/contact-us'
import { CATALOG_ID, HASHTAG, LANG_ID, STORE_ID } from '@/scripts/constants/parameters'
import wcs from '@/scripts/libs/wcs'
import { DAYS_OF_WEEK_SHORT } from '@/scripts/constants/physical-stores'
import { HoursOfOperation, FormattedHoursOfOperation } from '@/scripts/libs/wcs/contact-us'

import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'
import {
  CATALOG_ID_PLACEHOLDER,
  LANG_ID_PLACEHOLDER,
  STORE_ID_PLACEHOLDER,
} from '@/scripts/constants/placeholders'
import { WcsLocales } from './wcs/types/locales'
import RbDate from '@/utils/rb-date'
const { storeId, langId, catalogId, cancelOrderFaqId } = wcs.getStoreConfig()

let allLocales: any
RbDate.getAllLocales().then((locales) => {
  allLocales = locales
})

const getLocale = () => {
  const locale = navigator.language.split('-')
  const rootLocale = locale[0]

  return allLocales[rootLocale]
}

export class ContactUs {
  static buildCtaChannelLink({
    link,
    linkType,
    userLoggedIn,
  }: {
    link: undefined | string
    linkType: undefined | string
    userLoggedIn: boolean
  }): string {
    const {
      AS_PORTAL,
      FAQ,
      ORDER_RETURN_LINK,
      ORDER_STATUS,
      REPORT_FAKE_LINK,
      STELLA_FAQ,
      SUPERNOVA_FAQ,
      WARRANTY,
    } = contactLinkTypes

    switch (linkType) {
      case ORDER_RETURN_LINK:
        if (userLoggedIn) {
          return `/MyOrdersView?${CATALOG_ID}=${catalogId}&${LANG_ID}=${langId}&${STORE_ID}=${storeId}`
        } else {
          const localePath = ContactUs.getLocalePath()
          const url = `${window.origin}/${localePath}`
          return `${url}/MyOrders`
        }
      case STELLA_FAQ:
        return wcs.getEndPoint('stellaFaq') ?? HASHTAG
      case SUPERNOVA_FAQ:
        return wcs.getEndPoint('supernovaFaq') ?? HASHTAG
      case FAQ:
        return `${wcs.getEndPoint('faq')}${cancelOrderFaqId}`
      case AS_PORTAL:
      case WARRANTY:
        return wcs.getEndPoint('warranty') ?? HASHTAG
      case REPORT_FAKE_LINK:
        return wcs.getEndPoint('reportFakeURL') ?? HASHTAG
      case ORDER_STATUS:
        return `/TrackOrdersView?${CATALOG_ID}=${catalogId}&${LANG_ID}=${langId}&${STORE_ID}=${storeId}`
      default:
        return link ?? HASHTAG
    }
  }

  static buildChatChannelLink(link: string): string {
    if (!link) return ''

    const chatStoreId = ContactUs.getStoreId()
    return link
      .replace(CATALOG_ID_PLACEHOLDER, catalogId)
      .replace(LANG_ID_PLACEHOLDER, langId)
      .replace(STORE_ID_PLACEHOLDER, chatStoreId)
  }

  static getCallBackLink(): string {
    const callBackEndpoint = wcs.getEndPoint('callBack')
    if (!callBackEndpoint) return HASHTAG

    return callBackEndpoint
      .replace(CATALOG_ID_PLACEHOLDER, catalogId)
      .replace(LANG_ID_PLACEHOLDER, langId)
      .replace(STORE_ID_PLACEHOLDER, storeId)
  }

  static getFormAction(): string {
    return wcs.getEndPoint('contactFormAction')
  }

  static channelIsAvailableNow(channelHoursOfOperation: Array<HoursOfOperation>): boolean {
    const timezone = head(channelHoursOfOperation)?.hours?.timezone
    if (!timezone) return false

    const nowWithTimezone = RbDate.fromZonedTimeDate()
    const nowDayOfWeek = RbDate.formatDate(nowWithTimezone, 'eeeeee').toLowerCase()
    const nowHoursOfOperation = channelHoursOfOperation.find((shift) => shift.day === nowDayOfWeek)

    if (!nowHoursOfOperation) return false

    const startHour = RbDate.fromZonedTimeDate(
      `${RbDate.formatDate(nowWithTimezone, 'yyyy-MM-dd')}T${nowHoursOfOperation.hours.start}`
    )
    const endHour = RbDate.fromZonedTimeDate(
      `${RbDate.formatDate(nowWithTimezone, 'yyyy-MM-dd')}T${nowHoursOfOperation.hours.end}`
    )

    const nowIsAfterStartHour = RbDate.isAfterDate(nowWithTimezone, startHour)
    const nowIsBeforeEndHour = RbDate.isBeforeDate(nowWithTimezone, endHour)

    return nowIsAfterStartHour && nowIsBeforeEndHour
  }

  static buildDaysSeparator(): string {
    const { locale } = wcs.getStoreConfig()

    switch (locale) {
      case WcsLocales.ZH_CN:
        return '至'
      default:
        return ' - '
    }
  }

  static formatHoursOfOperation(
    hoursOfOperation: Array<HoursOfOperation> = []
  ): Array<HoursOfOperation> {
    if (!hoursOfOperation.length) return []

    const daysSeparator = ContactUs.buildDaysSeparator()

    const result: Array<HoursOfOperation> = []

    const formattedHoursOfOperation: Array<FormattedHoursOfOperation> = hoursOfOperation.map(
      (day) => {
        const startDate = RbDate.parseDate(day.hours.start, 'HH:mm')
        const endDate = RbDate.parseDate(day.hours.end, 'HH:mm')

        const formattedStartHour = RbDate.formatDate(startDate, 'p', {
          locale: getLocale(),
        })
        const formattedEndHour = RbDate.formatDate(endDate, 'p', {
          locale: getLocale(),
        })

        return {
          ...day,
          hours: {
            ...day.hours,
            start: formattedStartHour,
            end: formattedEndHour,
          },
          formattedHours: `${formattedStartHour} - ${formattedEndHour}`,
        }
      }
    )

    const hoursOfOperationGroupedByHours = [
      ...Object.values(groupBy(formattedHoursOfOperation, 'formattedHours')),
    ] as Array<Array<HoursOfOperation>>

    if (hoursOfOperationGroupedByHours.length === 1) {
      const daysWithSameHoursOfOperation = head(hoursOfOperationGroupedByHours)
      result.push({
        day: '',
        displayDay: `${
          (head(daysWithSameHoursOfOperation) as HoursOfOperation).displayDay
        }${daysSeparator}${(last(daysWithSameHoursOfOperation) as HoursOfOperation).displayDay}`,
        hours: (head(daysWithSameHoursOfOperation) as HoursOfOperation).hours,
      })
      return result
    }

    hoursOfOperationGroupedByHours.forEach((days) => {
      if (days.length === 1) {
        result.push(head(days) as HoursOfOperation)
        return result
      }

      let dayOfWeekIndex = 0
      let nextDayOfWeekIndex = 1
      let isDayInsideSequence = false
      let formattedDisplayDay = ''

      for (let i = 0; i < days.length - 1; i++) {
        dayOfWeekIndex = DAYS_OF_WEEK_SHORT.indexOf(days[i].day)
        nextDayOfWeekIndex = DAYS_OF_WEEK_SHORT.indexOf(days[i + 1].day)

        if (nextDayOfWeekIndex - dayOfWeekIndex === 1) {
          if (isDayInsideSequence) {
            continue
          } else {
            isDayInsideSequence = true
            formattedDisplayDay += `${days[i].displayDay}${daysSeparator}`
          }
        } else {
          if (isDayInsideSequence) {
            isDayInsideSequence = false
          }
          formattedDisplayDay += `${days[i].displayDay}, `
        }
      }

      formattedDisplayDay += (last(days) as HoursOfOperation).displayDay
      const daysHours = head(days)?.hours
      if (daysHours) {
        result.push({
          day: '',
          displayDay: formattedDisplayDay,
          hours: daysHours,
        })
      }
    })

    return result.map((days) => ({ ...days, day: '' }))
  }

  private static getStoreId = (): string => {
    if (storeId === WcsStoreId.CANADA) {
      return '4'
    }
    if (storeId === WcsStoreId.USA) {
      return '3'
    }
    return storeId
  }

  static getPrefix = (): string => {
    switch (storeId) {
      case WcsStoreId.BELGIUM:
        return '+32'
      case WcsStoreId.DENMARK:
        return '+45'
      case WcsStoreId.FRANCE:
        return '+33'
      case WcsStoreId.GERMANY:
        return '+49'
      case WcsStoreId.IRELAND:
        return '+353'
      case WcsStoreId.ITALY:
        return '+39'
      case WcsStoreId.NETHERLANDS:
        return '+31'
      case WcsStoreId.NORWAY:
        return '+47'
      case WcsStoreId.POLAND:
        return '+48'
      case WcsStoreId.PORTUGAL:
        return '+351'
      case WcsStoreId.SPAIN:
        return '+34'
      case WcsStoreId.SWITZERLAND:
        return '+41'
      case WcsStoreId.AUSTRIA:
        return '+43'
      case WcsStoreId.SWEDEN:
        return '+46'
      case WcsStoreId.UNITED_KINGDOM:
        return '+44'
      case WcsStoreId.AUSTRALIA:
        return '+61'
      case WcsStoreId.JAPAN:
        return '+81'
      case WcsStoreId.CHINA:
        return '+86'
      case WcsStoreId.HONG_KONG:
        return '+852'
      case WcsStoreId.KOREA:
        return '+82'
      default:
        return ''
    }
  }

  static isFieldFileAttachmentVisibleByCountry = (): boolean => {
    return fileAttachmentVisibleCountryList.includes(storeId)
  }

  static isFieldPhoneVisibleByCountry = (): boolean => {
    return phoneVisibleCountryList.includes(storeId)
  }

  static isEMEACountries = (): boolean => {
    return countriesWithTwothousandDigitsInTopic.includes(storeId)
  }

  static isCountryNameAndSurnameMandatoryField = (): boolean => {
    return countriesWithMandatoryNameAndSurnameField.includes(storeId)
  }

  private static getLocalePath = (): string => {
    const isMultiLanguageCountry = WcsStoreId.currentCountryIsMultiLanguage()
    const splitPathName = window.location?.pathname?.split('/')

    return isMultiLanguageCountry ? `${splitPathName[1]}/${splitPathName[2]}` : splitPathName[1]
  }
}
